import {
  keepPreviousData,
  useMutation,
  useQuery,
  type Query,
} from "@tanstack/react-query";
import {
  createAthleteLink,
  createRosterAssignment,
  createRosterAssignmentsBulk,
  deleteAthleteLink,
  endRosterAssignment,
  getAthlete,
  getAthleteFeedbackCollection,
  getAthletes,
  getMyAthletes,
  transferRosterAssignment,
} from "@/lib/api";
import {
  invalidateScope,
  queryClient,
  scopedKeyOf,
  unscopedKey,
  useScopedKey,
} from "@/lib/queryClient";
import { useAuthStore } from "@/stores/auth";
import type {
  AthleteDetail,
  AthleteDirectoryEntry,
  AthleteDirectoryStatusFilter,
  AthleteLinkCreate,
  FeedbackAthleteCollection,
  LinkedAthlete,
  RosterAssignmentBulkCreate,
  RosterAssignmentCreate,
  RosterAssignmentEnd,
  RosterAssignmentTransfer,
} from "@/lib/types";

/**
 * #908 — athlete directory, roster assignments and linked-athlete reads for
 * the NON-admin surfaces (coach Roster "Add player" / "Link account",
 * Account → Linked athletes, the athlete-first Feedback collection). The
 * club-console hooks live in `features/admin/hooks/useAdminData.ts` and
 * share the key factories + invalidation helper exported here, so no
 * feature file ever hand-rolls an athlete query key.
 *
 * Keys: the coach/account reads are SCOPED (`useScopedKey` →
 * `[domain, clubId, teamId, seasonId, extra?]`). The data is club-wide,
 * not team-wide, but the scoped tuple is what partitions the cache by
 * club, lets `invalidateScope()` refresh it with the rest of the roster
 * after a write and evicts it on team switch / sign-out
 * (`.agent-skills/tanstack-query-scope-keys.md`). The club-console reads
 * are unscoped-private `admin-*` domains like `admin-teams`: evicted at
 * the auth boundary, never whitelisted as public.
 */

/** Scope tuple for the non-React key builders (`scopedKeyOf`'s first argument). */
export type AthleteKeyScope = Parameters<typeof scopedKeyOf>[0];

export const ATHLETE_DOMAINS = {
  /** `GET /api/athletes` search (coach-side, D14). */
  directory: "athlete-directory",
  /** `GET /api/athletes/{id}` (coach-side). */
  detail: "athlete-detail",
  /** `GET /api/me/athletes` — the account's linked athletes. */
  meAthletes: "me-athletes",
  /** `GET /api/feedback/athletes/{id}/collection`. */
  feedbackCollection: "athlete-feedback-collection",
  /** Club console copies of the directory search + detail. */
  adminDirectory: "admin-athletes",
  adminDetail: "admin-athlete-detail",
} as const;

/** Scoped keys for the coach / account reads — use inside React via the
 * hooks below; these builders are for mutation callbacks, tests and
 * invalidation predicates. */
export const athleteKeys = {
  directory: (
    scope: AthleteKeyScope,
    query: string,
    status: AthleteDirectoryStatusFilter | null = null,
  ) =>
    scopedKeyOf(scope, ATHLETE_DOMAINS.directory, {
      query: query.trim(),
      status,
    }),
  detail: (scope: AthleteKeyScope, athleteId: string) =>
    scopedKeyOf(scope, ATHLETE_DOMAINS.detail, { id: athleteId }),
  meAthletes: (scope: AthleteKeyScope) =>
    scopedKeyOf(scope, ATHLETE_DOMAINS.meAthletes),
  feedbackCollection: (scope: AthleteKeyScope, athleteId: string) =>
    scopedKeyOf(scope, ATHLETE_DOMAINS.feedbackCollection, {
      athlete_id: athleteId,
    }),
};

/** Unscoped-private keys for the club console (`/admin/athletes`). */
export const adminAthleteKeys = {
  /** Prefix of every directory search — invalidate it to refresh them all. */
  lists: () => unscopedKey(ATHLETE_DOMAINS.adminDirectory),
  list: (query: string, status: AthleteDirectoryStatusFilter | null = null) =>
    unscopedKey(ATHLETE_DOMAINS.adminDirectory, { query: query.trim(), status }),
  /** Prefix of every detail read. */
  details: () => unscopedKey(ATHLETE_DOMAINS.adminDetail),
  detail: (athleteId: string) =>
    unscopedKey(ATHLETE_DOMAINS.adminDetail, { id: athleteId }),
};

/** Every cache domain that renders athletes or their assignments. Used as
 * key PREFIXES below, so each one matches every scope tuple it is keyed
 * under (a transfer touches a team other than the active one). */
const ATHLETE_READ_DOMAINS: readonly string[] = [
  // Coach roster rows — the assignments themselves (`useRoster`).
  "roster",
  ATHLETE_DOMAINS.directory,
  ATHLETE_DOMAINS.detail,
  ATHLETE_DOMAINS.meAthletes,
  ATHLETE_DOMAINS.feedbackCollection,
  ATHLETE_DOMAINS.adminDirectory,
  ATHLETE_DOMAINS.adminDetail,
  // `/api/me`: projected memberships and each team's `season` + `status`.
  "me",
  // Club-console reads that render projected memberships / roster rows:
  // the admin users list (`unscopedKey("admin-users")`), the Teams-page
  // memberships panel (`unscopedKey("admin-teams-memberships", { teamId })`)
  // and the People invite roster
  // (`unscopedKey("admin-people-invite-roster", { … })`).
  "admin-users",
  "admin-teams-memberships",
  "admin-people-invite-roster",
];

/** True for a detail read (club console or coach-side) of `athleteId`. */
function isAthleteDetailKey(key: readonly unknown[], athleteId: string): boolean {
  if (
    key[0] !== ATHLETE_DOMAINS.adminDetail &&
    key[0] !== ATHLETE_DOMAINS.detail
  )
    return false;
  return key.some(
    (part) =>
      !!part &&
      typeof part === "object" &&
      (part as { id?: unknown }).id === athleteId,
  );
}

/** Refresh every read that shows athletes or their assignments after a
 * directory, roster-assignment or account-link write. The active scope is
 * refreshed wholesale as well (roster KPIs, linkable users, …), matching
 * the legacy roster writes in `useRosterMutations`. After a delete, pass
 * `deletedAthleteId`: that athlete's own detail reads are left alone, so
 * a still-mounted detail page is never refetched into a 404 before it
 * navigates away. */
export async function invalidateAthleteReads(
  options: { deletedAthleteId?: string } = {},
): Promise<void> {
  const gone = options.deletedAthleteId;
  const keep = (q: Query) => !gone || !isAthleteDetailKey(q.queryKey, gone);
  await Promise.all([
    invalidateScope(),
    ...ATHLETE_READ_DOMAINS.map((domain) =>
      // A one-element tuple is a prefix filter here, not a query key.
      queryClient.invalidateQueries({ queryKey: [domain], predicate: keep }),
    ),
  ]);
}

/** Every route in this module is club-host only (404 on the apex) and
 * authenticated, so no read fires before `/api/me` resolved the host club. */
function useReadyOnClubHost(): boolean {
  return useAuthStore((s) => !s.hydrating && !!s.user && !!s.currentClub);
}

/** Directory search for the coach-side flows (D14): name, status and
 * team/season assignments only; `birth_year` is `null` unless the viewer
 * is a club admin or the athlete's own team staff. The server requires
 * club admin or `roster:manage` on a team of the club (403 otherwise) —
 * gate the UI on that capability and pass `enabled` for search-as-you-type. */
export function useAthleteDirectory(
  query: string,
  options?: { status?: AthleteDirectoryStatusFilter; enabled?: boolean },
) {
  const ready = useReadyOnClubHost();
  const status = options?.status ?? null;
  const queryKey = useScopedKey(ATHLETE_DOMAINS.directory, {
    query: query.trim(),
    status,
  });
  return useQuery<AthleteDirectoryEntry[]>({
    queryKey,
    queryFn: () => getAthletes({ query, status: status ?? undefined }),
    enabled: ready && (options?.enabled ?? true),
    // Keep the previous list on screen while the next keystroke's loads.
    placeholderData: keepPreviousData,
  });
}

/** One athlete with every assignment; `links` only when the viewer may
 * manage them (`can_manage_links`), `merges` for club admins (`can_merge`). */
export function useAthleteDetail(
  athleteId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  const ready = useReadyOnClubHost();
  const queryKey = useScopedKey(ATHLETE_DOMAINS.detail, {
    id: athleteId ?? null,
  });
  return useQuery<AthleteDetail>({
    queryKey,
    queryFn: () => getAthlete(athleteId as string),
    enabled: ready && !!athleteId && (options?.enabled ?? true),
  });
}

/** The signed-in account's linked athletes on this club host (self /
 * guardian / family) with the assignments it still holds a projected
 * membership for (D5) — Account → Linked athletes, the athlete picker. */
export function useMeAthletes(options?: { enabled?: boolean }) {
  const ready = useReadyOnClubHost();
  return useQuery<LinkedAthlete[]>({
    queryKey: useScopedKey(ATHLETE_DOMAINS.meAthletes),
    queryFn: getMyAthletes,
    enabled: ready && (options?.enabled ?? true),
  });
}

/** The athlete-first Feedback collection (§5.1): assignments grouped by
 * season then team, each read through the per-scope fences; the server
 * picks `view` (`recipient` for a linked account, `coach` for own-team
 * staff). An unavailable athlete is a 404 `collection_unavailable` — no
 * retry, and nothing is kept fresh across a scope change (`staleTime: 0`). */
export function useAthleteFeedbackCollection(
  athleteId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  const ready = useReadyOnClubHost();
  const queryKey = useScopedKey(ATHLETE_DOMAINS.feedbackCollection, {
    athlete_id: athleteId ?? null,
  });
  return useQuery<FeedbackAthleteCollection>({
    queryKey,
    queryFn: ({ signal }) =>
      getAthleteFeedbackCollection(athleteId as string, { signal }),
    enabled: ready && !!athleteId && (options?.enabled ?? true),
    retry: false,
    staleTime: 0,
  });
}

// ---------------------------------------------------------------------------
// Writes. Each returns the TanStack mutation (`mutate` / `mutateAsync`,
// `isPending`, `error`) and refreshes every athlete read on success — the
// returned promise is awaited by `mutateAsync`, so callers can navigate
// once the caches are fresh. Errors are `ApiError`s: use `errorMessage()`.
// ---------------------------------------------------------------------------

/** `POST /api/roster/assignments` — roster an existing athlete
 * (`athlete_id`) or create one inline (`athlete`) on `team_id`. */
export function useCreateAssignment() {
  return useMutation({
    mutationFn: (body: RosterAssignmentCreate) => createRosterAssignment(body),
    onSuccess: () => invalidateAthleteReads(),
  });
}

/** `POST /api/roster/assignments/bulk` — several existing athletes onto one
 * team, all-or-nothing (the carry-forward roster step). A row error's
 * detail is prefixed `Item N: …`. */
export function useBulkAssignments() {
  return useMutation({
    mutationFn: (body: RosterAssignmentBulkCreate) =>
      createRosterAssignmentsBulk(body),
    onSuccess: () => invalidateAthleteReads(),
  });
}

/** `POST /api/roster/assignments/{player_id}/end` — remove from team.
 * The confirmation copy must say this removes the athlete's access to this
 * season's team content (§5.4, D12). */
export function useEndAssignment() {
  return useMutation({
    mutationFn: ({
      playerId,
      body,
    }: {
      playerId: string;
      body?: RosterAssignmentEnd;
    }) => endRosterAssignment(playerId, body),
    onSuccess: () => invalidateAthleteReads(),
  });
}

/** `POST /api/roster/assignments/{player_id}/transfer` — end here, create
 * on `to_team_id`, one transaction; `roster:manage` on both teams. */
export function useTransferAssignment() {
  return useMutation({
    mutationFn: ({
      playerId,
      body,
    }: {
      playerId: string;
      body: RosterAssignmentTransfer;
    }) => transferRosterAssignment(playerId, body),
    onSuccess: () => invalidateAthleteReads(),
  });
}

/** `POST /api/athletes/{id}/links` — link an account to the ATHLETE (not
 * the roster row); the service projects it onto every assignment. */
export function useLinkAthleteAccount() {
  return useMutation({
    mutationFn: ({
      athleteId,
      body,
    }: {
      athleteId: string;
      body: AthleteLinkCreate;
    }) => createAthleteLink(athleteId, body),
    onSuccess: () => invalidateAthleteReads(),
  });
}

/** `DELETE /api/athletes/{id}/links/{link_id}` — revoke an account link
 * and its projected memberships on every assignment. */
export function useRevokeAthleteLink() {
  return useMutation({
    mutationFn: ({ athleteId, linkId }: { athleteId: string; linkId: number }) =>
      deleteAthleteLink(athleteId, linkId),
    onSuccess: () => invalidateAthleteReads(),
  });
}
